/**
 * Capture + undo helpers for a sketch object's persisted transform.
 *
 * `capturePlacementValues` (moved out of SketchObjectTool so gizmo bindings
 * can share it) reads the placement half; `captureSketchTransformValues`
 * adds the size half (width/depth) — together everything a transform-gizmo
 * drag can change, restorable as ONE undo unit via
 * `createSketchTransformSnapshot`.
 */

import {
  getSketchObjectAnchorProperty,
  getSketchObjectRotationProperty,
  getSketchObjectWidthProperty,
  getSketchObjectDepthProperty,
  getSketchObjectWallEdgeIdProperty,
  getSketchObjectWallOffsetProperty,
  getSketchObjectWallSideProperty,
  getSketchObjectWallInsetProperty,
} from '@pointorama/potree-shared';
import { Snapshot } from '../../personas/tools/ToolUndoRedoManager';
import { getConstraintStrategy } from './constraintRegistry';
import type { RegisteredSketchObjectType } from '../registry';
import type { ConstraintSpec } from '../types';
import type { PlacementValues } from './types';

/** Current persisted placement values of an object (move undo, drag baseline). */
export function capturePlacementValues(objectId: string): PlacementValues {
  const wallEdgeId = getSketchObjectWallEdgeIdProperty(objectId).value || '';
  return {
    anchor: getSketchObjectAnchorProperty(objectId).value,
    rotation: getSketchObjectRotationProperty(objectId).value ?? 0,
    wall: wallEdgeId
      ? {
          edgeId: wallEdgeId,
          offset: getSketchObjectWallOffsetProperty(objectId).value ?? 0,
          side: getSketchObjectWallSideProperty(objectId).value,
          inset: getSketchObjectWallInsetProperty(objectId).value ?? 0,
        }
      : null,
  };
}

/** Placement + size — everything a gizmo drag can change, in one undo unit. */
export interface SketchTransformValues extends PlacementValues {
  width: number;
  depth: number;
}

export function captureSketchTransformValues(objectId: string, type: RegisteredSketchObjectType): SketchTransformValues {
  return {
    ...capturePlacementValues(objectId),
    width: getSketchObjectWidthProperty(objectId).value ?? type.defaultSize.width,
    depth: getSketchObjectDepthProperty(objectId).value ?? type.defaultSize.depth,
  };
}

/** Whether two captured transforms differ beyond float noise. */
export function sketchTransformChanged(a: SketchTransformValues, b: SketchTransformValues): boolean {
  const EPS = 1e-6;
  if (Math.abs(a.rotation - b.rotation) > EPS || Math.abs(a.width - b.width) > EPS || Math.abs(a.depth - b.depth) > EPS) return true;
  const anchorA = a.anchor ?? [];
  const anchorB = b.anchor ?? [];
  if (anchorA.length !== anchorB.length) return true;
  if (anchorA.some((p, i) => Math.abs(p.x - anchorB[i].x) > EPS || Math.abs(p.y - anchorB[i].y) > EPS)) return true;
  if (!!a.wall !== !!b.wall) return true;
  if (a.wall && b.wall) {
    if (a.wall.edgeId !== b.wall.edgeId || a.wall.side !== b.wall.side) return true;
    if (Math.abs(a.wall.offset - b.wall.offset) > EPS || Math.abs(a.wall.inset - b.wall.inset) > EPS) return true;
  }
  return false;
}

/**
 * Undo snapshot toggling a full sketch transform (placement via the type's
 * constraint strategy, plus width/depth) between before/after.
 */
export function createSketchTransformSnapshot(objectId: string, constraintKind: ConstraintSpec['kind'], before: SketchTransformValues, after: SketchTransformValues): Snapshot {
  let applyBefore = true;
  const snapshot: Snapshot = {
    restore: (): Snapshot => {
      const values = applyBefore ? before : after;
      try {
        getConstraintStrategy(constraintKind).writePlacement(objectId, values);
        getSketchObjectWidthProperty(objectId).value = values.width;
        getSketchObjectDepthProperty(objectId).value = values.depth;
      } catch {
        /* property service not ready */
      }
      applyBefore = !applyBefore;
      return snapshot;
    },
  };
  return snapshot;
}
